import React from "react";

import { AccordionItemProps, ActionItem, ActionItems, IconItems, PlayerProp, Positions } from "../../components";
import { PlayerStyle } from "../../components/tool-palette/tool-palette";
import {
  ball,
  cone,
  cutAngle,
  cutAngleCurve,
  cutAngleSCurve,
  cutCurve,
  cutCurveAngle,
  cutSCurve,
  cutStraight,
  dribbleAngle,
  dribbleAngleCurve,
  dribbleAngleSCurve,
  dribbleCurve,
  dribbleCurveAngle,
  dribbleHandoff,
  dribbleSCurve,
  dribbleStraight,
  flag,
  hoop,
  pass1,
  pass2,
  pass3,
  pass4,
  pass5,
  passCurve,
  passCurve1,
  passCurve2,
  passCurve3,
  passCurve4,
  passCurve5,
  passDribble,
  passStraight,
  screenAngle,
  screenAngleCurve,
  screenAngleSCurve,
  screenCurve,
  screenCurveAngle,
  screenSCurve,
  screenStraight,
  textbox,
} from "../../images";
import {
  ToolPaletteTextBoxId,
  getGlobalToolPane,
  toolPaletteShadingIds,
  toolPaletteTextBoxIds,
} from "../global/tool-palette-global";

/** ================================
 * Tool Palette Type Definitions
 ================================ */
/** ======== Player ======== */
export const bbToolPalettePlayerIds = [
  "BB_PLAYER_1",
  "BB_PLAYER_2",
  "BB_PLAYER_3",
  "BB_PLAYER_4",
  "BB_PLAYER_5",
  "BB_PLAYER_POSSESSION_1",
  "BB_PLAYER_POSSESSION_2",
  "BB_PLAYER_POSSESSION_3",
  "BB_PLAYER_POSSESSION_4",
  "BB_PLAYER_POSSESSION_5",
  "BB_PLAYER_FILL_1",
  "BB_PLAYER_FILL_2",
  "BB_PLAYER_FILL_3",
  "BB_PLAYER_FILL_4",
  "BB_PLAYER_FILL_5",
  "BB_PLAYER_DEF_1",
  "BB_PLAYER_DEF_2",
  "BB_PLAYER_DEF_3",
  "BB_PLAYER_DEF_4",
  "BB_PLAYER_DEF_5",
] as const;
export type BB_ToolPalettePlayerId = (typeof bbToolPalettePlayerIds)[number];
export const isBBPlayerId = (id: BB_ToolPaletteMarkerId): id is BB_ToolPalettePlayerId =>
  bbToolPalettePlayerIds.includes(id as BB_ToolPalettePlayerId);

/** ======== Misc ======== */
export const bbToolPaletteMiscIds = ["MISC_HOOP", "MISC_BALL", "MISC_FLAG", "MISC_CONE"] as const;
export type BB_ToolPaletteMiscId = (typeof bbToolPaletteMiscIds)[number];
export const isBBMiscId = (id: BB_ToolPaletteMarkerId): id is BB_ToolPaletteMiscId =>
  bbToolPaletteMiscIds.includes(id as BB_ToolPaletteMiscId);

/** ======== All Markers ======== */
export const bbToolPaletteMarkerIds = [
  ...bbToolPalettePlayerIds,
  ...bbToolPaletteMiscIds,
  ...toolPaletteTextBoxIds,
  ...toolPaletteShadingIds,
] as const;
export type BB_ToolPaletteMarkerId = (typeof bbToolPaletteMarkerIds)[number];
export const isBBMarkerId = (id: BB_ToolPaletteStencilId): id is BB_ToolPaletteMarkerId =>
  bbToolPaletteMarkerIds.includes(id as BB_ToolPaletteMarkerId);

/** ======== Action ======== */
/** ======== Cut ======== */
export const bbToolPaletteCutIds = [
  "CUT_STRAIGHT",
  "CUT_CURVE",
  "CUT_ANGLE",
  "CUT_ANGLE_CURVE",
  "CUT_CURVE_ANGLE",
  "CUT_S_CURVE",
  "CUT_ANGLE_S_CURVE",
] as const;
export type BB_ToolPaletteCutId = (typeof bbToolPaletteCutIds)[number];
export const isBBCutId = (id: BB_ToolPaletteActionId): id is BB_ToolPaletteCutId =>
  bbToolPaletteCutIds.includes(id as BB_ToolPaletteCutId);

/** ======== Pass ======== */
export const bbToolPalettePassIds = [
  "PASS_STRAIGHT",
  "PASS_CURVE",
  "PASS_HASHPASS_STRAIGHT_1",
  "PASS_HASHPASS_STRAIGHT_2",
  "PASS_HASHPASS_STRAIGHT_3",
  "PASS_HASHPASS_STRAIGHT_4",
  "PASS_HASHPASS_STRAIGHT_5",
  "PASS_HASHPASS_CURVE_1",
  "PASS_HASHPASS_CURVE_2",
  "PASS_HASHPASS_CURVE_3",
  "PASS_HASHPASS_CURVE_4",
  "PASS_HASHPASS_CURVE_5",
] as const;
export type BB_ToolPalettePassId = (typeof bbToolPalettePassIds)[number];
export const isBBPassId = (id: BB_ToolPaletteActionId): id is BB_ToolPalettePassId =>
  bbToolPalettePassIds.includes(id as BB_ToolPalettePassId);

/** ======== DHO ======== */
export const bbToolPaletteDHOIds = ["PASS_HANDOFF", "DHO"] as const;
export type BB_ToolPaletteDHOId = (typeof bbToolPaletteDHOIds)[number];
export const isBBDHOId = (id: BB_ToolPaletteActionId): id is BB_ToolPaletteDHOId =>
  bbToolPaletteDHOIds.includes(id as BB_ToolPaletteDHOId);

/** ======== Screen ======== */
export const bbToolPaletteScreenIds = [
  "SCREEN_STRAIGHT",
  "SCREEN_CURVE",
  "SCREEN_ANGLE",
  "SCREEN_ANGLE_CURVE",
  "SCREEN_CURVE_ANGLE",
  "SCREEN_S_CURVE",
  "SCREEN_ANGLE_S_CURVE",
] as const;
export type BB_ToolPaletteScreenId = (typeof bbToolPaletteScreenIds)[number];
export const isBBScreenId = (id: BB_ToolPaletteActionId): id is BB_ToolPaletteScreenId =>
  bbToolPaletteScreenIds.includes(id as BB_ToolPaletteScreenId);

/** ======== Dribble ======== */
export const bbToolPaletteDribbleIds = [
  "DRIBBLE_STRAIGHT",
  "DRIBBLE_CURVE",
  "DRIBBLE_ANGLE",
  "DRIBBLE_ANGLE_CURVE",
  "DRIBBLE_CURVE_ANGLE",
  "DRIBBLE_S_CURVE",
  "DRIBBLE_ANGLE_S_CURVE",
] as const;
export type BB_ToolPaletteDribbleId = (typeof bbToolPaletteDribbleIds)[number];
export const isBBDribbleId = (id: BB_ToolPaletteActionId): id is BB_ToolPaletteDribbleId =>
  bbToolPaletteDribbleIds.includes(id as BB_ToolPaletteDribbleId);

/** ======== Actions ======== */
export const bbToolPaletteActionIds = [
  ...bbToolPaletteCutIds,
  ...bbToolPalettePassIds,
  ...bbToolPaletteScreenIds,
  ...bbToolPaletteDribbleIds,
  ...bbToolPaletteDHOIds,
] as const;
export type BB_ToolPaletteActionId = (typeof bbToolPaletteActionIds)[number];
export const isBBActionId = (id: BB_ToolPaletteStencilId): id is BB_ToolPaletteActionId =>
  bbToolPaletteActionIds.includes(id as BB_ToolPaletteActionId);

/** ======== All Stencils ======== */
export const bbToolPaletteStencilIds = [...bbToolPaletteMarkerIds, ...bbToolPaletteActionIds] as const;
export type BB_ToolPaletteStencilId = (typeof bbToolPaletteStencilIds)[number];
export const isStringBBToolPaletteStencilId = (id: string): id is BB_ToolPaletteStencilId =>
  bbToolPaletteStencilIds.includes(id as BB_ToolPaletteStencilId);

/** ================================
 * Tool Palette Items
 ================================ */
/** ======== Players ======== */
export const basketballPlayerItems: PlayerProp<BB_ToolPalettePlayerId>[][] = [
  [
    { id: "BB_PLAYER_1", label: "1" },
    { id: "BB_PLAYER_2", label: "2" },
    { id: "BB_PLAYER_3", label: "3" },
    { id: "BB_PLAYER_4", label: "4" },
    { id: "BB_PLAYER_5", label: "5" },
  ],
  [
    { id: "BB_PLAYER_POSSESSION_1", label: "1", style: PlayerStyle.BORDER },
    { id: "BB_PLAYER_POSSESSION_2", label: "2", style: PlayerStyle.BORDER },
    { id: "BB_PLAYER_POSSESSION_3", label: "3", style: PlayerStyle.BORDER },
    { id: "BB_PLAYER_POSSESSION_4", label: "4", style: PlayerStyle.BORDER },
    { id: "BB_PLAYER_POSSESSION_5", label: "5", style: PlayerStyle.BORDER },
  ],
  [
    { id: "BB_PLAYER_FILL_1", label: "1", style: PlayerStyle.FILL },
    { id: "BB_PLAYER_FILL_2", label: "2", style: PlayerStyle.FILL },
    { id: "BB_PLAYER_FILL_3", label: "3", style: PlayerStyle.FILL },
    { id: "BB_PLAYER_FILL_4", label: "4", style: PlayerStyle.FILL },
    { id: "BB_PLAYER_FILL_5", label: "5", style: PlayerStyle.FILL },
  ],
  [
    { id: "BB_PLAYER_DEF_1", label: "x1" },
    { id: "BB_PLAYER_DEF_2", label: "x2" },
    { id: "BB_PLAYER_DEF_3", label: "x3" },
    { id: "BB_PLAYER_DEF_4", label: "x4" },
    { id: "BB_PLAYER_DEF_5", label: "x5" },
  ],
];

/** ======== Cut ======== */
export const cutItems: ActionItem<BB_ToolPaletteCutId>[] = [
  { id: "CUT_STRAIGHT", label: "Straight", iconUrl: cutStraight },
  { id: "CUT_CURVE", label: "Curve", iconUrl: cutCurve },
  { id: "CUT_ANGLE", label: "Angle", iconUrl: cutAngle },
  { id: "CUT_ANGLE_CURVE", label: "Angle w/ Curve", iconUrl: cutAngleCurve },
  { id: "CUT_CURVE_ANGLE", label: "Curve w/ Angle", iconUrl: cutCurveAngle },
  { id: "CUT_S_CURVE", label: "S Curve", iconUrl: cutSCurve },
  { id: "CUT_ANGLE_S_CURVE", label: "Angle w/ S Curve", iconUrl: cutAngleSCurve },
];

/** ======== Pass ======== */
export const passItems: ActionItem<BB_ToolPalettePassId | BB_ToolPaletteDHOId>[] = [
  { id: "PASS_STRAIGHT", label: "Straight", iconUrl: passStraight },
  { id: "PASS_CURVE", label: "Curve", iconUrl: passCurve },
  { id: "PASS_HANDOFF", label: "Dribble Handoff", iconUrl: passDribble },
  { id: "PASS_HASHPASS_STRAIGHT_1", label: "Pass 1", iconUrl: pass1 },
  { id: "PASS_HASHPASS_STRAIGHT_2", label: "Pass 2", iconUrl: pass2 },
  { id: "PASS_HASHPASS_STRAIGHT_3", label: "Pass 3", iconUrl: pass3 },
  { id: "PASS_HASHPASS_STRAIGHT_4", label: "Pass 4", iconUrl: pass4 },
  { id: "PASS_HASHPASS_STRAIGHT_5", label: "Pass 5", iconUrl: pass5 },
  { id: "PASS_HASHPASS_CURVE_1", label: "Curve 1", iconUrl: passCurve1 },
  { id: "PASS_HASHPASS_CURVE_2", label: "Curve 2", iconUrl: passCurve2 },
  { id: "PASS_HASHPASS_CURVE_3", label: "Curve 3", iconUrl: passCurve3 },
  { id: "PASS_HASHPASS_CURVE_4", label: "Curve 4", iconUrl: passCurve4 },
  { id: "PASS_HASHPASS_CURVE_5", label: "Curve 5", iconUrl: passCurve5 },
];

/** ======== Screen ======== */
export const screenItems: ActionItem<BB_ToolPaletteScreenId>[] = [
  { id: "SCREEN_STRAIGHT", label: "Straight", iconUrl: screenStraight },
  { id: "SCREEN_CURVE", label: "Curve", iconUrl: screenCurve },
  { id: "SCREEN_ANGLE", label: "Angle", iconUrl: screenAngle },
  { id: "SCREEN_ANGLE_CURVE", label: "Angle w/ Curve", iconUrl: screenAngleCurve },
  { id: "SCREEN_CURVE_ANGLE", label: "Curve w/ Angle", iconUrl: screenCurveAngle },
  { id: "SCREEN_S_CURVE", label: "S Curve", iconUrl: screenSCurve },
  { id: "SCREEN_ANGLE_S_CURVE", label: "Angle w/ S Curve", iconUrl: screenAngleSCurve },
];

/** ======== Dribble ======== */
export const dribbleItems: ActionItem<BB_ToolPaletteDribbleId>[] = [
  { id: "DRIBBLE_STRAIGHT", label: "Straight", iconUrl: dribbleStraight },
  { id: "DRIBBLE_CURVE", label: "Curve", iconUrl: dribbleCurve },
  { id: "DRIBBLE_ANGLE", label: "Angle", iconUrl: dribbleAngle },
  { id: "DRIBBLE_ANGLE_CURVE", label: "Angle w/ Curve", iconUrl: dribbleAngleCurve },
  { id: "DRIBBLE_CURVE_ANGLE", label: "Curve w/ Angle", iconUrl: dribbleCurveAngle },
  { id: "DRIBBLE_S_CURVE", label: "S Curve", iconUrl: dribbleSCurve },
  { id: "DRIBBLE_ANGLE_S_CURVE", label: "Angle w/ S Curve", iconUrl: dribbleAngleSCurve },
];

/** ======== Misc ======== */
export const miscellaneousItems: ActionItem<BB_ToolPaletteMiscId | ToolPaletteTextBoxId | BB_ToolPaletteDHOId>[] = [
  { id: "MISC_HOOP", label: "Hoop", iconUrl: hoop },
  { id: "MISC_BALL", label: "Ball", iconUrl: ball },
  { id: "MISC_FLAG", label: "Flag", iconUrl: flag },
  { id: "MISC_CONE", label: "Cone", iconUrl: cone },
  { id: "MISC_TEXTBOX", label: "Textbox", iconUrl: textbox },
  { id: "DHO", label: "Dribble Handoff", iconUrl: dribbleHandoff },
];

const extendedToolPalette: AccordionItemProps[] = [
  { type: "STANDARD", id: "pass-actions", label: "Pass", bodyRender: <ActionItems items={passItems} /> },
  { type: "STANDARD", id: "screen-actions", label: "Screen", bodyRender: <ActionItems items={screenItems} /> },
  { type: "STANDARD", id: "dribble-actions", label: "Dribble", bodyRender: <ActionItems items={dribbleItems} /> },
];

export interface GetToolPaneItemsProps {
  actionPaletteEnabled: boolean;
}

export const getBasketballToolPalette = ({
  actionPaletteEnabled = false,
}: GetToolPaneItemsProps): AccordionItemProps[] => {
  return [
    {
      type: "STANDARD",
      id: "positions",
      label: "Positions",
      bodyRender: <Positions positions={basketballPlayerItems} />,
    },
    {
      type: "STANDARD",
      id: "cut-actions",
      label: actionPaletteEnabled ? "Actions" : "Cut",
      bodyRender: <ActionItems items={cutItems} />,
    },
    ...(!actionPaletteEnabled ? extendedToolPalette : []),
    {
      type: "STANDARD",
      id: "miscellaneous",
      label: "Miscellaneous",
      bodyRender: <IconItems items={miscellaneousItems} />,
    },
    ...getGlobalToolPane(),
  ];
};
